import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Mail, Phone, MapPin, Send, Loader2, Check,
  HelpCircle, Briefcase, AlertCircle, MessageSquare, ShieldCheck, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from 'react-i18next';

const BASE_URL = window.location.origin;
const WHATSAPP_NUMBER = '48507334540';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Cześć, mam pytanie o ApisAI...')}`;

const topics = [
  { id: 'oferta', label: 'Pytanie o ofertę / cennik', icon: Briefcase },
  { id: 'technical', label: 'Problem techniczny', icon: AlertCircle },
  { id: 'cooperation', label: 'Współpraca / partnerstwo', icon: MessageSquare },
  { id: 'feature', label: 'Propozycja nowej funkcji', icon: HelpCircle },
  { id: 'other', label: 'Inne', icon: Mail },
];

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    topics: [],
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleTopic = (topicId) => {
    setForm(prev => ({
      ...prev,
      topics: prev.topics.includes(topicId)
        ? prev.topics.filter(t => t !== topicId)
        : [...prev.topics, topicId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || form.topics.length === 0 || !form.message) {
      toast.error('Wypełnij wszystkie wymagane pola i wybierz temat');
      return;
    }

    setSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));

    console.log('[CONTACT FORM]', {
      ...form,
      topics: form.topics.map(t => topics.find(topic => topic.id === t)?.label),
      timestamp: new Date().toISOString()
    });

    setSubmitted(true);
    toast.success('Wiadomość wysłana! Odpowiemy w ciągu 24h.');
    setSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Kontakt — ApisAI | Skontaktuj się z nami</title>
        <meta name="description" content="Masz pytania o ApisAI? Skontaktuj się z nami. Odpowiadamy w ciągu 24 godzin." />
        <link rel="canonical" href={`${BASE_URL}/kontakt`} />
      </Helmet>

      <div className="min-h-screen bg-[#060610]">
        {/* Grid bg */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-950/20 via-[#060610] to-[#060610]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(249,115,22,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,.15) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        </div>

        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#060610]/70 border-b border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.svg" alt="ApisAI" className="h-7 sm:h-8" />
              <span className="text-base sm:text-lg font-bold tracking-tight text-white">ApisAI</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/logowanie" className="text-gray-500 hover:text-white transition text-sm hidden sm:block">Zaloguj się</Link>
              <Link to="/rejestracja" className="px-4 py-2 bg-orange-500 hover:bg-orange-400 rounded-lg font-semibold text-sm transition-all shadow-lg shadow-orange-500/25 text-white">
                Zacznij teraz
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-28 pb-12 px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Skontaktuj się z nami
              </h1>
              <p className="text-lg text-gray-400">
                Masz pytania? Chętnie pomożemy. Odpowiadamy w ciągu 24 godzin.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="pb-16 px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-12">
              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="lg:col-span-2 space-y-6"
              >
                <div>
                  <h2 className="text-xl font-semibold text-white mb-5">Dane kontaktowe</h2>

                  <div className="space-y-4">
                    {/* WhatsApp — primary */}
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 hover:border-emerald-400/50 transition-colors group"
                      data-testid="contact-whatsapp"
                    >
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <svg className="w-6 h-6 text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-400 font-medium">Szybki kontakt</p>
                        <p className="text-white group-hover:text-emerald-300 transition-colors font-semibold">WhatsApp</p>
                      </div>
                    </a>

                    <a
                      href="mailto:kontakt@apisai.pl"
                      className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-orange-500/30 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                        <Mail className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Email</p>
                        <p className="text-white group-hover:text-orange-400 transition-colors">kontakt@apisai.pl</p>
                      </div>
                    </a>

                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-orange-500/30 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-orange-400/20 flex items-center justify-center">
                        <Phone className="w-6 h-6 text-orange-300" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Telefon / WhatsApp</p>
                        <p className="text-white group-hover:text-orange-400 transition-colors">+48 507 334 540</p>
                      </div>
                    </a>

                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10">
                      <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Lokalizacja</p>
                        <p className="text-white">Warszawa, Polska</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                  <h3 className="font-semibold text-white mb-1 text-sm">Najszybsza odpowiedź?</h3>
                  <p className="text-xs text-gray-400">
                    Napisz na WhatsApp — odpowiadamy w kilka minut.
                  </p>
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="lg:col-span-3"
              >
                <div className="backdrop-blur-xl bg-[#0c0c1a]/80 border border-orange-500/15 rounded-2xl p-8 shadow-2xl shadow-orange-500/5">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                        <Check className="w-10 h-10 text-emerald-400" />
                      </div>
                      <h3 className="text-2xl font-semibold text-white mb-3">Wiadomość wysłana!</h3>
                      <p className="text-gray-400 mb-6">
                        Dziękujemy za kontakt. Odpowiemy najszybciej jak to możliwe.
                      </p>
                      <Link to="/">
                        <Button className="bg-orange-500 hover:bg-orange-400 rounded-xl border-0">
                          Wróć na stronę główną
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Imię i nazwisko *</Label>
                          <Input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Jan Kowalski"
                            required
                            className="bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Email *</Label>
                          <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="jan@email.pl"
                            required
                            className="bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-300 mb-2 block text-sm">Telefon (opcjonalnie)</Label>
                        <Input
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="+48 123 456 789"
                          className="bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl h-12 text-white placeholder:text-zinc-500 transition-all"
                        />
                      </div>

                      {/* Topics */}
                      <div>
                        <Label className="text-gray-300 mb-3 block text-sm">Temat wiadomości *</Label>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {topics.map((topic) => (
                            <label
                              key={topic.id}
                              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all text-sm ${
                                form.topics.includes(topic.id)
                                  ? 'bg-orange-500/15 border-orange-500/40'
                                  : 'bg-white/[0.03] border-white/10 hover:border-white/20'
                              }`}
                            >
                              <Checkbox
                                checked={form.topics.includes(topic.id)}
                                onCheckedChange={() => toggleTopic(topic.id)}
                                className="border-white/30 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                              />
                              <topic.icon className={`w-4 h-4 ${
                                form.topics.includes(topic.id) ? 'text-orange-400' : 'text-gray-500'
                              }`} />
                              <span className={form.topics.includes(topic.id) ? 'text-white' : 'text-gray-300'}>
                                {topic.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-300 mb-2 block text-sm">Wiadomość *</Label>
                        <Textarea
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          placeholder="Opisz swoje pytanie lub problem..."
                          required
                          rows={4}
                          className="bg-white/5 border-white/10 focus:border-orange-500/50 focus:bg-white/10 rounded-xl text-white placeholder:text-zinc-500 transition-all resize-none"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg hover:shadow-orange-500/30 rounded-xl h-12 text-base font-bold transition-all"
                      >
                        {submitting ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Wyślij wiadomość
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        Wysyłając formularz, zgadzasz się na przetwarzanie danych zgodnie z{' '}
                        <Link to="/polityka-prywatnosci" className="text-orange-400 hover:underline">
                          polityką prywatności
                        </Link>.
                      </p>
                    </form>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Footer — matching landing */}
        <footer className="relative z-10 border-t border-white/5 pt-10 pb-6">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 mb-8">
              <div className="col-span-2 sm:col-span-1">
                <div className="flex items-center gap-2 mb-3">
                  <img src="/logo.svg" alt="ApisAI" className="h-7" />
                  <span className="font-bold text-white">ApisAI</span>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed mb-3">
                  ApisAI pomaga hodowlom i małym firmom zdobywać klientów prościej.
                </p>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Bezpiecznie. RODO. SSL.</span>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Produkt</h4>
                <div className="space-y-2">
                  <Link to="/#jak-dziala" className="block text-sm text-gray-300 hover:text-orange-400 transition">Jak to działa</Link>
                  <Link to="/#cennik" className="block text-sm text-gray-300 hover:text-orange-400 transition">Cennik</Link>
                  <Link to="/logowanie" className="block text-sm text-gray-300 hover:text-orange-400 transition">Logowanie</Link>
                  <Link to="/rejestracja" className="block text-sm text-gray-300 hover:text-orange-400 transition">Utwórz konto</Link>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">Informacje</h4>
                <div className="space-y-2">
                  <Link to="/regulamin" className="block text-sm text-gray-300 hover:text-orange-400 transition">Regulamin</Link>
                  <Link to="/polityka-prywatnosci" className="block text-sm text-gray-300 hover:text-orange-400 transition">Polityka prywatności</Link>
                  <Link to="/kontakt" className="block text-sm text-gray-300 hover:text-orange-400 transition">Kontakt</Link>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-xs text-gray-400">&copy; ApisAI — zdobywaj klientów prościej</span>
              <span className="text-xs text-gray-500">Made with love{' '}
                <a href="https://apisdesign.online" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-400 transition">
                  apisdesign.online
                </a>
              </span>
            </div>
          </div>
        </footer>
      </div>

      {/* WhatsApp floating button */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-400 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/40 hover:scale-110 transition-transform"
        data-testid="whatsapp-float"
        aria-label="WhatsApp"
      >
        <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </>
  );
}
